/**
 * Протокол событий: сервер → пульт. Одно описание на обе стороны (§8.1).
 *
 * Пульт не опрашивает сервер, а держит сокет. События нумеруются seq и лежат
 * в журнале realtime_events, поэтому после обрыва пропущенное досылается,
 * а снапшот перезапрашивается только когда разрыв не уложился в окно реплея.
 */
import { z } from 'zod';
// ─────────────────── Протокол сокета: seq, реплей, desync (§8.1) ───────────────────
//
// Событие пишется в realtime_events внутри бизнес-транзакции и получает seq.
// После обрыва клиент присылает hello{lastSeq}: пропущенное досылается по журналу,
// а если разрыв не уложился в окно — приходит desync, и клиент берёт снапшот по REST.
// Без seq «тебе выдан номер» теряется молча у диспетчера, у которого моргнул сокет.
/** Типы событий журнала. Строка события — часть контракта с фронтом */
export const REALTIME_EVENT_TYPES = [
    'assignment.issued',
    'assignment.closed',
    'assignment.needs_label',
    'call.started',
    'call.ringing',
    'call.answered',
    'call.ended',
    'call.recording_ready',
    'queue.stats',
    'distribution.finished',
    'dispatcher.line_changed',
    'settings.changed',
    'trunk.registration_lost',
    'session.revoked',
];
export const realtimeEventTypeSchema = z.enum(REALTIME_EVENT_TYPES);
/**
 * Комнаты выдаёт сервер: строка от клиента — это запрос, а не факт.
 * Каждая желаемая комната прогоняется через canSubscribe(user, room),
 * фактический список возвращается в кадре ready.
 */
export const realtimeRoomSchema = z
    .string()
    .regex(/^(user:\d+|dispatcher:\d+|role:(admin|supervisor)|queue:global)$/, 'неизвестный формат комнаты');
export function userRoom(userId) {
    return `user:${userId}`;
}
export function dispatcherRoom(dispatcherId) {
    return `dispatcher:${dispatcherId}`;
}
export function roleRoom(role) {
    return `role:${role}`;
}
export const QUEUE_GLOBAL_ROOM = 'queue:global';
/** Кадры от браузера */
export const clientMsgSchema = z.discriminatedUnion('t', [
    z.object({ t: z.literal('hello'), lastSeq: z.number().int().min(0).optional(), rooms: z.array(realtimeRoomSchema).max(50) }),
    z.object({ t: z.literal('subscribe'), rooms: z.array(realtimeRoomSchema).max(50) }),
    z.object({ t: z.literal('unsubscribe'), rooms: z.array(realtimeRoomSchema).max(50) }),
    z.object({ t: z.literal('pong'), ts: z.number().int() }),
]);
/** Кадры от сервера */
export const serverMsgSchema = z.discriminatedUnion('t', [
    z.object({ t: z.literal('ready'), seq: z.number().int().min(0), rooms: z.array(z.string()), serverTime: z.number().int() }),
    z.object({
        t: z.literal('event'),
        seq: z.number().int().min(0),
        room: z.string(),
        type: realtimeEventTypeSchema,
        payload: z.unknown(),
    }),
    z.object({ t: z.literal('ping'), ts: z.number().int() }),
    // gap — событий уже нет в журнале, too_old — разрыв длиннее окна реплея
    z.object({ t: z.literal('desync'), reason: z.enum(['gap', 'too_old']) }),
    z.object({ t: z.literal('error'), code: z.string(), message: z.string() }),
]);
/** Heartbeat сокета: молчание дольше двух периодов — соединение считается мёртвым */
export const REALTIME_HEARTBEAT_MS = 20_000;
/** Окно реплея: за его пределами клиенту дешевле взять снапшот, чем догонять журнал */
export const REALTIME_RESUME_MAX_EVENTS = 500;
export const REALTIME_RESUME_MAX_AGE_MS = 10 * 60 * 1000;
//# sourceMappingURL=realtime.js.map